import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import { LoadingBlur, Panel } from '@ics-portal/guardian';
import { type NotificationSource } from '@gql/graphql';
import InfoCardWrapper from '@components/_molecules/InfoCards/Layout/InfoCardWrapper';
import { GET_SECURITY_NOTIFICATIONS_FOR_COMPONENT } from '@api/queries/notifications';
import { type StringOption } from '@models/Option';
import { type ComponentProps } from '../../models/ComponentProps';
import { NotificationsList } from './NotificationsList';

export function NotificationsDetailsTab({
    component,
}: ComponentProps): ReactNode {
    const { data, loading } = useQuery(
        GET_SECURITY_NOTIFICATIONS_FOR_COMPONENT,
        {
            variables: { id: component.id },
        },
    );

    const notificationsBySource = useMemo(() => {
        const notifications =
            data?.notifications.edges
                .map((edge) => edge.node)
                .filter(Boolean) ?? [];

        return notifications.reduce((groups, notification) => {
            const sourceNotifications = groups.get(notification.source) ?? [];

            sourceNotifications.push({
                value: notification.id,
                label: notification.title,
            });
            groups.set(notification.source, sourceNotifications);

            return groups;
        }, new Map<NotificationSource, StringOption[]>());
    }, [data]);

    const isEmpty = notificationsBySource.size === 0;

    return (
        <InfoCardWrapper title="Security notifications">
            <LoadingBlur loading={loading}>
                {isEmpty ? 'No security notifications found.' : null}
                {!isEmpty &&
                    Array.from(notificationsBySource.entries()).map(
                        ([source, notifications]) => (
                            <Panel key={source} header={source}>
                                <NotificationsList
                                    notifications={notifications}
                                />
                            </Panel>
                        ),
                    )}
            </LoadingBlur>
        </InfoCardWrapper>
    );
}
